import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ArrowRight, Compass, Package, Sparkles } from "lucide-react";
import { useLanguageNavigation } from "@/components/LanguageRouter";
import { useContactForm } from "@/components/ContactFormModal";
import heroImage from "@/assets/hero-abstract.webp";
// ?no-inline pins both badges to file emission: at ~3.5-5 KB they sit under
// Vite's 4 KB inline limit (which ?url does not bypass), and inlining would
// ride the entry path's JS (CWS-196/199) instead of a parallel, cacheable
// request.
import clutchBadge from "@/assets/badges/clutch-2024.png?no-inline";
import vendorlandBadge from "@/assets/badges/vendorland-2025.png?no-inline";
import astraZenecaLogo from "@/assets/clients/astra_zeneca.png";
import boschLogo from "@/assets/clients/bosch.png";
import civittaLogo from "@/assets/clients/civitta.png";
import dynamiqLogo from "@/assets/clients/dynamiq.png";
import perconaLogo from "@/assets/clients/percona.png";
import periodixLogo from "@/assets/clients/periodix.png";
import testRigorLogo from "@/assets/clients/test_rigor.png";
import xfusionLogo from "@/assets/clients/xfusion.png";
import axbioLogo from "@/assets/clients/axbio.png";

// Curated subset for the homepage hero strip; broader logo sets can appear elsewhere.
const clients = [
  { name: "AstraZeneca", logo: astraZenecaLogo, width: 320, height: 80 },
  { name: "Bosch", logo: boschLogo, width: 339, height: 80 },
  { name: "Civitta", logo: civittaLogo, width: 295, height: 80 },
  { name: "Dynamiq", logo: dynamiqLogo, width: 320, height: 80 },
  { name: "Percona", logo: perconaLogo, width: 318, height: 80 },
  { name: "Periodix", logo: periodixLogo, width: 203, height: 80 },
  { name: "testRigor", logo: testRigorLogo, width: 348, height: 80 },
  { name: "xFusion Technologies", logo: xfusionLogo, width: 200, height: 75 },
  { name: "Axbio", logo: axbioLogo, width: 272, height: 80 },
];

const allClients = [...clients, ...clients];

const servicesConfig = [
  {
    icon: Compass,
    titleKey: "hero.services.aiDiscovery",
    descriptionKey: "services.aiDiscovery.description",
    href: "/services/consulting",
  },
  {
    icon: Package,
    titleKey: "hero.services.fixedPrice",
    descriptionKey: "services.fixedPrice.description",
    href: "/services/fixed-price",
  },
  {
    icon: Sparkles,
    titleKey: "hero.services.enablement",
    descriptionKey: "services.enablement.description",
    href: "/services/enablement",
  },
];

export function HeroSection() {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguageNavigation();
  const { openContactForm } = useContactForm();

  return (
    <section className="relative flex flex-col overflow-hidden">
      {/* Hero image background */}
      <div className="absolute inset-0 -z-10">
        <img 
          src={heroImage} 
          alt="" 
          width={1600}
          height={900}
          fetchPriority="high"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/60" />
      </div>

      <div className="container px-4 sm:px-6 lg:px-8 py-10 md:py-14 flex-1 flex flex-col justify-center">
        {/* Badge pill — above the grid so both columns start at the same height */}
        <div className="mb-5">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-sm font-medium text-primary">
            <span className="flex h-2 w-2 rounded-full bg-primary animate-pulse" />
            {t("hero.badge")}
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 xl:gap-14 items-stretch w-full">
          {/* Left: Content */}
          <div className="lg:col-span-7 xl:pr-6 flex flex-col">
            <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-6xl leading-[1.1] mb-5">
              {t("hero.title")}{" "}
              <span className="text-primary">{t("hero.subtitle")}</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-muted-foreground max-w-xl mb-6 leading-relaxed">
              {t("hero.description")}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8">
              <Button size="lg" className="h-12 px-6 sm:px-8 text-base group w-full sm:w-auto" onClick={() => openContactForm({ page: "Home", button: "Book a Call", buttonPosition: "hero" })}>
                {t("common.bookACall")}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button size="lg" variant="outline" className="h-12 px-6 sm:px-8 text-base w-full sm:w-auto" asChild>
                <a href={getLocalizedPath("/case-studies")}>
                  {t("whatWeBuild.cta.caseStudies")}
                </a>
              </Button>
            </div>

            {/* Trust stats */}
            <div className="text-sm text-muted-foreground font-medium mb-8">
              {t("hero.trust")}
            </div>

            {/* Links + Badges row — pushed to bottom via mt-auto */}
            {/* Recognition badges — centered on mobile, inline on desktop */}
            <div className="flex flex-col items-center sm:flex-row sm:items-end gap-6 sm:gap-8 mt-auto">
              <div className="flex flex-col gap-3 w-full sm:w-auto">
                <a href="https://clutch.co/profile/shelpuk-ai-technology-consulting" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center sm:justify-start gap-2 px-4 py-2 rounded-lg border border-border/60 bg-card/80 text-sm font-medium text-foreground hover:border-primary/40 hover:bg-primary/5 transition-all">
                  <span className="flex h-2 w-2 rounded-full bg-primary" />
                  {t("hero.clutchBadge")}
                </a>
                <a href="https://vendorland.com/companies/artificial-intelligence" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center sm:justify-start gap-2 px-4 py-2 rounded-lg border border-border/60 bg-card/80 text-sm font-medium text-foreground hover:border-primary/40 hover:bg-primary/5 transition-all">
                  <span className="flex h-2 w-2 rounded-full bg-primary" />
                  {t("hero.vendorlandBadge")}
                </a>
              </div>
              <div className="flex items-end justify-center gap-6">
                <a href="https://clutch.co/profile/shelpuk-ai-technology-consulting" target="_blank" rel="noopener noreferrer" className="transition-opacity hover:opacity-80">
                  <img
                    src={clutchBadge}
                    alt="Clutch Top Generative AI Company 2024"
                    width={145}
                    height={196}
                    decoding="async"
                    className="h-24 sm:h-28 w-auto object-contain"
                  />
                </a>
                <a href="https://vendorland.com/companies/artificial-intelligence" target="_blank" rel="noopener noreferrer" className="transition-opacity hover:opacity-80">
                  <img
                    src={vendorlandBadge}
                    alt="Vendorland Top AI Consulting Company 2025"
                    width={174}
                    height={196}
                    decoding="async"
                    className="h-24 sm:h-28 w-auto object-contain"
                  />
                </a>
              </div>
            </div>
          </div>

          {/* Right: Service Cards — stretch full height, cards distributed evenly */}
          <div className="lg:col-span-5 flex flex-col justify-between gap-4">
            {servicesConfig.map((service, index) => (
              <a
                key={service.titleKey}
                href={getLocalizedPath(service.href)}
                className="group relative flex-1 flex flex-col justify-center rounded-2xl bg-card/90 backdrop-blur-sm border border-border/50 p-6 transition-all duration-300 hover:border-primary/40 hover:shadow-xl hover:bg-card hover:-translate-y-0.5"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="absolute right-6 top-1/2 -translate-y-1/2 text-muted-foreground/50 transition-all duration-300 group-hover:text-primary group-hover:translate-x-1">
                  <ArrowRight className="h-5 w-5" />
                </div>
                <div className="flex items-start gap-4 pr-10">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary transition-all duration-300 group-hover:bg-primary group-hover:text-primary-foreground">
                    <service.icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-[1.0625rem] font-semibold leading-snug text-foreground transition-colors group-hover:text-primary [text-wrap:balance]">
                      {t(service.titleKey)}
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {t(service.descriptionKey)}
                    </p>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Client logos carousel */}
      <div className="pb-10 md:pb-14">
      <div className="container mb-8">
          <p className="text-center text-sm font-medium uppercase tracking-widest text-muted-foreground">
            {t("clients.trustedBy", "Trusted by industry leaders")}
          </p>
        </div>
        <div className="relative overflow-hidden mb-2">
          <div className="absolute left-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-r from-background to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-l from-background to-transparent z-10" />
          <div className="inline-flex items-center gap-12 md:gap-16 animate-scroll">
            {allClients.map((client, i) => (
              <div
                key={`${client.name}-${i}`}
                className="flex-shrink-0 flex items-center justify-center h-12 w-[140px] md:w-[180px]"
              >
                {/* The first visible logos should paint immediately; duplicates further in the loop can stay lazy. */}
                <img
                  src={client.logo}
                  alt={client.name}
                  width={client.width}
                  height={client.height}
                  loading={i < 4 ? "eager" : "lazy"}
                  decoding="async"
                  className="max-h-10 max-w-full object-contain opacity-60 hover:opacity-100 transition-opacity duration-300 grayscale"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
